import {
    ReviewAverageType,
    ReviewResponseType,
    ReviewType,
    ReviewUser,
    ReviewUserGender,
    ReviewsInfoType,
    SkeepersExtrasItem,
    SkeepersRatingResponse,
    SkeepersReviewResponse,
} from '@app-types/review';

const transformToReviewType = (data: ReviewResponseType): ReviewType => {
    return {
        note: Number(data.rate),
        title: data.product_review_title,
        shortMessage: data.review.slice(0, 280),
        message: data.review,
        created_at: new Date(data.review_date),
        user: getUser(data),
        isRecommended: data.product_review_recommandation_ami.toLowerCase() === 'oui',
        recommendedFor: data.product_review_recommandation_telephone,
        syndication: {
            image: data.medias,
            name: data.brand_name,
        },
    };
};

const getUser = (data: ReviewResponseType): ReviewUser => {
    const user: ReviewUser = {
        login: data.firstname,
        isVerified: true,
        ageLabel: data.brand_review_age,
    };

    if (data.product_review_sexe) {
        user.gender = data.product_review_sexe === 'Homme' ? ReviewUserGender.MALE : ReviewUserGender.FEMALE;
    }

    return user;
};

export const mapToReviewInfo = (data: ReviewResponseType[], offset: number, limit: number): ReviewsInfoType => {
    const reviewInfo: ReviewsInfoType = {
        note: 0,
        reviewCount: 0,
        averages: {},
        reviews: [],
    };
    const reviewCount = data.length;
    let note = 0;
    let averageAuto = 0;
    let averageUse = 0;
    let averageDesign = 0;
    let averageMulti = 0;

    reviewInfo.reviewCount = reviewCount;

    const reviews = data.slice(offset, limit + offset);

    // calculate averages
    data.forEach((reviewData) => {
        note += +reviewData.rate;
        averageAuto += +reviewData.product_review_autonomie;
        averageUse += +reviewData.product_review_facilitedutilisation;
        averageDesign += +reviewData.product_review_design;
        averageMulti += +reviewData.product_review_qualite_appareil2;
    });

    // process reviews
    reviewInfo.reviews = reviews.map((review) => transformToReviewType(review));

    reviewInfo.note = calculateAverage(note, reviewCount);

    reviewInfo.averages = {
        multimedia: { label: 'Multimedia', note: calculateAverage(averageMulti, reviewCount) },
        use: { label: 'Utilisation', note: calculateAverage(averageUse, reviewCount) },
        autonomy: { label: 'Autonomie', note: calculateAverage(averageAuto, reviewCount) },
        design: { label: 'Design', note: calculateAverage(averageDesign, reviewCount) },
    };

    return reviewInfo;
};

export const calculateAverage = (score: number, count: number): number => {
    if (count === 0) return 0;
    return Math.round((score * 10) / count) / 10;
};

/**
 * Round a number to 1 decimal place
 */
const roundToOneDecimal = (value: number): number => {
    return Math.round(value * 10) / 10;
};

// ============================================
// Skeepers API v2 Mapping Functions
// ============================================

/**
 * Mapping for quality criteria from extras field
 * Supports both API v1 keys and API v2 labels (PRODUCT_REVIEW_*)
 */
const QUALITY_CRITERIA_MAPPING: Record<string, { key: string; label: string }> = {
    // API v1 keys
    autonomie: { key: 'autonomy', label: 'Autonomie' },
    autonomy: { key: 'autonomy', label: 'Autonomie' },
    design: { key: 'design', label: 'Design' },
    utilisation: { key: 'use', label: 'Utilisation' },
    facilitedutilisation: { key: 'use', label: 'Utilisation' },
    ease_of_use: { key: 'use', label: 'Utilisation' },
    multimedia: { key: 'multimedia', label: 'Multimedia' },
    qualite_appareil: { key: 'multimedia', label: 'Multimedia' },
    qualite_appareil2: { key: 'multimedia', label: 'Multimedia' },
    // API v2 labels (productSummary.extras)
    product_review_autonomie: { key: 'autonomy', label: 'Autonomie' },
    product_review_design: { key: 'design', label: 'Design' },
    product_review_facilitedutilisation: { key: 'use', label: 'Utilisation' },
    product_review_qualite_appareil2: { key: 'multimedia', label: 'Multimedia' },
};

/**
 * Maps API v2 ratings response to ReviewAverageType[]
 * Note: API v2 returns average on 1-5 scale (no multiplication needed)
 */
export const mapSkeepersRatingsToAverages = (ratings: SkeepersRatingResponse[]): ReviewAverageType[] => {
    return ratings.map((rating) => ({
        gencode: rating.product_variation_id || rating.product_ean || '',
        rate: rating.average,
        count: rating.count_total,
    }));
};

/**
 * Maps API v2 reviews response to ReviewsInfoType
 */
export const mapSkeepersReviewsToInfo = (
    reviews: SkeepersReviewResponse[],
    productSummary: SkeepersRatingResponse | undefined,
    offset: number,
    limit: number,
): ReviewsInfoType => {
    const reviewInfo: ReviewsInfoType = {
        note: 0,
        reviewCount: 0,
        averages: {},
        reviews: [],
    };

    if (productSummary) {
        reviewInfo.note = roundToOneDecimal(productSummary.average);
        reviewInfo.reviewCount = productSummary.count_total;
        reviewInfo.averages = extractQualityCriteria(productSummary.extras);
    } else if (reviews.length > 0) {
        reviewInfo.reviewCount = reviews.length;
        const totalRate = reviews.reduce((sum, r) => sum + r.review_rate, 0);
        reviewInfo.note = calculateAverage(totalRate, reviews.length);
        reviewInfo.averages = calculateQualityAveragesFromReviews(reviews);
    }

    const paginatedReviews = reviews.slice(offset, offset + limit);
    reviewInfo.reviews = paginatedReviews.map(transformSkeepersReview);

    return reviewInfo;
};

/**
 * Extract quality criteria from extras array
 * Supports both API v1 format (key/value) and API v2 format (label/average)
 */
const extractQualityCriteria = (extras?: SkeepersExtrasItem[]): Record<string, { label: string; note: number }> => {
    const averages: Record<string, { label: string; note: number }> = {};

    if (!extras) return getDefaultQualityCriteria();

    for (const extra of extras) {
        // API v2 uses 'label', API v1 uses 'key'
        const lookupKey = (extra.label || extra.key || '').toLowerCase();
        const mapping = QUALITY_CRITERIA_MAPPING[lookupKey];

        // API v2 uses 'average', API v1 uses 'value'
        const noteValue = extra.average ?? extra.value;

        if (mapping && typeof noteValue === 'number') {
            averages[mapping.key] = {
                label: mapping.label,
                note: roundToOneDecimal(noteValue),
            };
        }
    }

    return {
        multimedia: averages.multimedia || { label: 'Multimedia', note: 0 },
        use: averages.use || { label: 'Utilisation', note: 0 },
        autonomy: averages.autonomy || { label: 'Autonomie', note: 0 },
        design: averages.design || { label: 'Design', note: 0 },
    };
};

/**
 * Calculate quality averages from individual review extras
 */
const calculateQualityAveragesFromReviews = (
    reviews: SkeepersReviewResponse[],
): Record<string, { label: string; note: number }> => {
    const sums: Record<string, { total: number; count: number; label: string }> = {
        autonomy: { total: 0, count: 0, label: 'Autonomie' },
        use: { total: 0, count: 0, label: 'Utilisation' },
        design: { total: 0, count: 0, label: 'Design' },
        multimedia: { total: 0, count: 0, label: 'Multimedia' },
    };

    for (const review of reviews) {
        if (!review.extras) continue;

        for (const [key, extra] of Object.entries(review.extras)) {
            const normalizedKey = key.toLowerCase();
            const mapping = QUALITY_CRITERIA_MAPPING[normalizedKey];

            if (mapping && typeof extra.value === 'number') {
                const sumEntry = sums[mapping.key];
                if (sumEntry) {
                    sumEntry.total += extra.value;
                    sumEntry.count += 1;
                }
            }
        }
    }

    const averages: Record<string, { label: string; note: number }> = {};
    for (const [key, data] of Object.entries(sums)) {
        averages[key] = {
            label: data.label,
            note: data.count > 0 ? calculateAverage(data.total, data.count) : 0,
        };
    }

    return averages;
};

const getDefaultQualityCriteria = (): Record<string, { label: string; note: number }> => ({
    multimedia: { label: 'Multimedia', note: 0 },
    use: { label: 'Utilisation', note: 0 },
    autonomy: { label: 'Autonomie', note: 0 },
    design: { label: 'Design', note: 0 },
});

/**
 * Transform single API v2 review to ReviewType
 */
const transformSkeepersReview = (review: SkeepersReviewResponse): ReviewType => {
    return {
        note: review.review_rate,
        title: extractReviewTitle(review),
        shortMessage: review.review_content.slice(0, 280),
        message: review.review_content,
        created_at: new Date(review.review_date || review.publish_date),
        user: extractUser(review),
        isRecommended: true, // Default to true - not available in API v2
        recommendedFor: '', // Default to empty string - not available in API v2
        syndication: {
            image: review.medias?.[0]?.url || '',
            name: review.syndicated_review_source || review.product_brand || '',
        },
        pros: extractModeration(review.moderations, 'pros'),
        cons: extractModeration(review.moderations, 'cons'),
    };
};

const extractReviewTitle = (review: SkeepersReviewResponse): string => {
    const content = review.review_content;
    const firstSentence = content.split(/[.!?]/)[0] || '';
    return firstSentence.length <= 100 ? firstSentence : content.slice(0, 100) + '...';
};

const extractUser = (review: SkeepersReviewResponse): ReviewUser => {
    const extras = review.extras || {};
    const genderValue = extras['PRODUCT_REVIEW_SEXE']?.value as string;
    const ageValue = extras['BRAND_REVIEW_AGE']?.value as string;

    return {
        login: review.author_firstname || 'Anonyme',
        isVerified: review.is_verified,
        gender:
            genderValue === 'Homme'
                ? ReviewUserGender.MALE
                : genderValue === 'Femme'
                  ? ReviewUserGender.FEMALE
                  : undefined,
        ageLabel: ageValue || '',
    };
};

const extractModeration = (moderations: SkeepersReviewResponse['moderations'], type: string): string | undefined => {
    if (!moderations) return undefined;
    const mod = moderations.find((m) => m.type.toLowerCase() === type);
    return mod?.content;
};
